import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from "redux-persist";
import createIndexedDBStorage from "redux-persist-indexeddb-storage";
import storage from "redux-persist/lib/storage";

import { monitorAdminRole } from "./observers/adminObserver";
import { monitorClientRole } from "./observers/clientObserver";
import { monitorTokenExpiration, monitorAuthState } from "./observers/authObserver";

import authReducer from "./slices/authSlice";
import adminReducer from "./slices/adminSlice";
import clientReducer from "./slices/clientSlice";
import moderatorReducer from "./slices/moderatorSlice";
import quickbooksReportsReducer from "./slices/quickbooksReportsSlice";
import aiReducer from "./slices/aiSlice";

import { authApi } from "./apis/authApi";
import { adminApi } from "./apis/adminApi";
import { clientApi } from "./apis/clientApi";
import { moderatorApi } from "./apis/moderatorApi";
import { aiApi } from "./apis/aiApi";

import { aiListener } from "./listeners/aiListener";

const indexedDBStorage = createIndexedDBStorage({
  name: 'AallieDB',
  version: 1,
});

const authPersistConfig = {
  key: 'auth',
  storage,
  whitelist: ['Token', 'Role', 'UserData', 'Flag', 'TrialFlag', 'isAuthenticated'],
};

const clientPersistConfig = {
  key: 'client',
  storage: indexedDBStorage,
  condition: (state) => {
    const role = state.auth.Role;
    return role === "Free_User" || role === "Pro_User";
  }
};

const adminPersistConfig = {
  key: 'admin',
  storage: indexedDBStorage,
  condition: (state) => {
    const role = state.auth.Role;
    return role === "Admin" || role === "Super_Admin";
  }
};

const moderatorPersistConfig = {
  key: 'moderator',
  storage: indexedDBStorage,
  condition: (state) => {
    const role = state.auth.Role;
    return role === "Moderator" || role === "Super_Moderator";
  }
};

const aiPersistConfig = {
  key: 'ai',
  storage,
  whitelist: ['provider'],
};

const rootReducer = combineReducers({
  [authApi.reducerPath]: authApi.reducer,
  [adminApi.reducerPath]: adminApi.reducer,
  [clientApi.reducerPath]: clientApi.reducer,
  [moderatorApi.reducerPath]: moderatorApi.reducer,
  [aiApi.reducerPath]: aiApi.reducer,
  auth: persistReducer(authPersistConfig, authReducer),
  admin: persistReducer(adminPersistConfig, adminReducer),
  client: persistReducer(clientPersistConfig, clientReducer),
  moderator: persistReducer(moderatorPersistConfig, moderatorReducer),
  quickbooksReports: quickbooksReportsReducer,
  ai: persistReducer(aiPersistConfig, aiReducer),
});

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({
    serializableCheck: {
      ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      // transcribeAudio is called with FormData, which RTK Query keeps as the mutation's originalArgs
      ignoredPaths: [/^aiApi\.mutations\.[^.]+\.originalArgs$/],
    },
  })
    .prepend(aiListener.middleware)
    .concat(authApi.middleware, clientApi.middleware, adminApi.middleware, moderatorApi.middleware, aiApi.middleware),
});

monitorClientRole(store);
monitorAdminRole(store);
monitorTokenExpiration(store);
monitorAuthState(store);

export const persistor = persistStore(store);
export default store;